// Meal Recipe Composer — Template-first (Strategy E Phase 1, ADR-0013)
//
// Sostituisce il sampling random pool-based del composer ADR-0006/Track A.
// Pesca ricette autentiche da public.meal_recipes (2083 ricette IT misya
// importate in Fase 0) e le scala per kcal target del slot, riusando food_id
// del pool 690 alimenti per coerenza con la persistenza meal_plan_ingredients.
//
// Architettura:
//   1. preloadCompatibleRecipes(sb, profile) → query + filter (1× per genAll)
//   2. composeFromRecipe(sb, slot, profile, history, kcalTarget, foodCache)
//      → pick recipe + scale anchor qty + ricalcola macro da foods
//   3. Per slot=col: pattern hardcoded IT (yogurt+granola, pane+marmellata…)
//      con fallback alle 8 ricette colazione DB.
//
// Output: item compatibile con UI/builders { nm, sb, st, ig, mc{k,p,c,f} }.
// Campi _* metadata interni (recipe_id, variety_key, anchor names) per
// history anti-rotation; vengono rimossi da composeMealSkeleton pre-emit.

import { BASELINE_NUTRITIONAL_RULES, SLOT_KCAL_RANGE_RATIO } from './method-facts.js';

// ─── shortName helper (duplicato da generators.js per evitare circular) ─
const SHORTNAME_SKIP_WORDS = new Set(['di','da','con','a','al','alla','del','della','dello','e','o','in','su','la','il','lo']);
function shortName(canonical) {
  if (!canonical) return '';
  let s = canonical.replace(/\([^)]*\)/g, '').trim();
  const allTokens = s.toLowerCase().split(/\s+/).filter(Boolean);
  const significant = allTokens.filter(t => !SHORTNAME_SKIP_WORDS.has(t));
  if (significant.length === 0) return allTokens[0] || '';
  return significant.slice(0, 2).join(' ');
}

// ─── Module-scope cache ricette compatibili ─────────────────────────────
let _recipesCache = null;
let _cacheKey = null;

export function clearRecipeCache() {
  _recipesCache = null;
  _cacheKey = null;
}

// Mappa slot composer → slot DB meal_recipes (con fallback)
const SLOT_TO_DB = {
  col:  ['colazione'],                                          // 8 ricette + breakfast patterns
  sna1: ['spuntino'],                                           // 339 ricette
  sna2: ['spuntino'],
  pra:  ['pranzo', 'pranzo_o_cena', 'pranzo_leggero'],          // 655+336+164 = 1155 ricette
  cen:  ['cena', 'pranzo_o_cena']                               // 441+336 = 777 ricette
};

// ─── Phase 1.5: filtri qualità (app è "per la salute" ma non stretta) ─────
// Salumi/carni processate (lardo/wurstel/salame/pancetta/mortadella ecc.)
// restano pescabili: la loro frequenza naturale nel pool è già bassa (~12%
// degli spuntini, meno sui main meal), quindi appariranno occasionalmente
// come ricetta autentica senza saturare il piano. Filtro per esclusione
// è gestito via excluded_intolleranze del profilo utente.

// Snack: NO main-meal carbs (pasta/riso/gnocchi/lasagne sono pranzi taggati
// male come 'spuntino' nello scrape misya). Spuntino italiano vero =
// frutta/yogurt/pane+spread/frutta secca/cracker. Pasta NON è spuntino.
const SNACK_BLACKLIST_CARBS = [
  'pasta', 'spaghetti', 'penne', 'fusilli', 'rigatoni', 'tagliatelle',
  'lasagne', 'cannelloni', 'tortellini', 'ravioli', 'gnocchi',
  'fettuccine', 'linguine', 'paccheri', 'orecchiette', 'pici',
  'cous cous', 'cous-cous', 'couscous', 'cuscus',
  'risotto'
  // Nota: 'riso' singolo non blacklist (compare in poke bowl/riso freddo
  // che sono snack/lunch validi). Solo risotto + paste sono no-snack.
];

// Frittate/omelette sono cene leggere non spuntini → no per slot=spuntino
const SNACK_BLACKLIST_DISH_TERMS = [
  'frittata', 'omelette'
];

// Phase 1.7: range kcal slot proporzionale al kcalTarget dello slot.
// Prima erano hardcoded (col 200-450, ecc.) — inadeguati per utenti con
// target alto (es. 2660 kcal → col target 665, ma cap 450 = solo ricette
// piccole → scaling x1.48 forzato → distorsione piatto).
//
// Nuovo: range = [0.6 × slotKcal, 1.5 × slotKcal] = ricette entro ±50% del
// target slot. Scaling factor naturale 0.67-1.67, distorsione minima.
// SLOT_KCAL_RANGE_RATIO vive in method-facts.js (fonte unica, letta anche da /metodo).

function slotKcalRange(slot, slotKcalTarget) {
  if (!slotKcalTarget || slotKcalTarget <= 0) return null;
  return {
    min: slotKcalTarget * SLOT_KCAL_RANGE_RATIO.min,
    max: slotKcalTarget * SLOT_KCAL_RANGE_RATIO.max
  };
}

// Phase 1.7: client-side intolerance check (defense in depth).
// excluded_intolleranze nel DB è popolato da tagger scrape Fase 0, ma alcuni
// taggin sono incompleti (es. ricette con "Burro" non sempre hanno 'lattosio').
// Questo dictionary fa scan keyword sui canonical_name di ingredients_jsonb.
// False positives gestiti via DAIRY_SAFE / GLUTEN_SAFE patterns (vegan/nut subs).
// Phase 5b (Meal Data Quality Overhaul): keyword lists allargate per coprire
// plurali italiani. DB canonical_name è prevalentemente plurale (Acciughe,
// Gamberi, Sardine, Calamari), match singolare ('acciuga'.in.acciughe) falliva.
// Aggiunte forme plurali + sinonimi inglesi comuni in scrape source cross-
// cultural.
const INTOL_KEYWORDS_COMPOSER = {
  carne:           ['manzo','vitello','bistecca','bovino','pollo','polli','tacchino','tacchini','maiale','suino','agnello','agnelli','anatra','anatre','coniglio','conigli','bresaola','salame','salami','salsiccia','salsicce','wurstel','prosciutto','prosciutti','speck','lardo','pancetta','pancette','mortadella','coppa','guanciale','porchetta','salume','salumi','carne','carni'],
  pesce:           ['salmone','salmoni','tonno','tonni','merluzzo','merluzzi','orata','orate','branzino','branzini','sgombro','sgombri','sogliola','sogliole','acciuga','acciughe','alici','sardine','sardina','pesce','pesci','trota','trote','spigola','spigole','dentice','denti','pesce spada','spada','nasello','naselli','rana pescatrice','platessa','platesse','halibut','salmon','tuna','cod','anchov','sardin','tilapia'],
  crostacei:       ['gambero','gamberi','gamberetti','gamberetto','gambon','gamberoni','scampo','scampi','aragosta','aragoste','astice','astici','granchio','granchi','mazzancolle','mazzancolla','crostaceo','crostacei','shrimp','prawn','lobster','crab'],
  molluschi:       ['cozze','cozza','vongole','vongola','calamaro','calamari','seppia','seppie','polpo','polpi','polipi','moscardini','muscoli','mussel','mussels','clam','octopus','squid','calamari','mitili','tartufi di mare','ostrica','ostriche','oyster','capesante','capasanta','scallop'],
  lattosio:        ['latte','yogurt','ricotta','mozzarella','formaggio','parmigiano','pecorino','feta','burro','panna','mascarpone','scamorza','provolone','gorgonzola','stracchino','caprino','burrata','cheddar','grana','quark','skyr'],
  latticini:       ['latte','yogurt','ricotta','mozzarella','formaggio','parmigiano','pecorino','feta','burro','panna','mascarpone','scamorza','provolone','gorgonzola','stracchino','caprino','burrata','cheddar','grana','quark','skyr'],
  latte:           ['latte','yogurt','ricotta','mozzarella','formaggio','parmigiano','pecorino','feta','burro','panna','mascarpone','scamorza','provolone','gorgonzola','stracchino','caprino','burrata','cheddar','grana','quark','skyr'],
  uova:            ['uova','uovo','albume','tuorlo','frittata','omelette'],
  glutine:         ['pasta','pane','farro','orzo','segale','seitan','couscous','cracker','biscotti','muesli','granola','focaccia','pizza','fette biscottate','grano','frumento','spaghetti','penne','rigatoni','tortellini','ravioli','gnocchi'],
  'frutta secca':  ['mandorle','noci','nocciole','pistacchi','anacardi','arachidi','pinoli','frutta secca'],
  frutta_secca:    ['mandorle','noci','nocciole','pistacchi','anacardi','arachidi','pinoli','frutta secca'],
  sesamo:          ['sesamo','tahini','tahina'],
  soia:            ['soia','tofu','tempeh','edamame','salsa soia','yogurt di soia','seitan'],
  miele:           ['miele'],
  // Phase 5b (Meal Data Quality Overhaul): categorie nutrizionali per
  // BASELINE_NUTRITIONAL_RULES (regole soft penalty, non bloccanti).
  carne_rossa:     ['manzo','vitello','bistecca','bovino','agnello','agnelli','maiale','suino','cavallo','beef','pork','lamb','veal'],
  carne_bianca:    ['pollo','polli','tacchino','tacchini','coniglio','conigli','chicken','turkey'],
  salumi:          ['bresaola','salame','salami','salsiccia','salsicce','wurstel','prosciutto','prosciutti','speck','lardo','pancetta','pancette','mortadella','coppa','guanciale','porchetta','salume','salumi','ham','bacon','sausage','salami'],
  legumi:          ['ceci','cece','lenticchie','lenticchia','fagioli','fagiolo','piselli','pisello','fave','fava','cicerchia','soia','lupini','edamame','beans','chickpea','lentil','garbanzo'],
  formaggi_stagionati: ['parmigiano','parmigiano reggiano','pecorino','grana','asiago','provolone','cheddar','gorgonzola','feta','caciocavallo','taleggio'],
  fritti:          ['fritto','fritti','frittura','fritta','fried','tempura','frittata'],
  dolci:           ['cioccolato','torta','crostata','tiramisu','tiramisù','panettone','pandoro','marmellata','nutella','crema','crostatina','biscotti','cookie','cake','dessert']
};

// Sostituti vegani/nut che matchano keyword latticini ma sono safe
const DAIRY_SAFE_PATTERN = /\b(soia|mandorl|arachid|cocco|avena|anacard|nocciol)/;

// Phase 5b: alias semantici. L'utente in linguaggio comune intende:
//   "no pesce"  → no pesce + crostacei (gamberi) + molluschi (calamari, polpi)
//   "no carne"  → solo carne (lascia pesce ok per pescetariano)
//   "no glutine"→ solo glutine
// Senza alias, `preferenze.no=['pesce']` matchava solo la lista 'pesce',
// lasciando passare gamberi/scampi/calamari → utente vedeva pesce in piano
// pur dicendo "no pesce". Bug critico (analogia con intolleranza glutine).
const INTOL_SEMANTIC_ALIAS = {
  pesce: ['pesce', 'crostacei', 'molluschi'],
};

function ingredientViolatesIntolerance(ing, intolRaw) {
  const name = (ing.canonical_name || '').toLowerCase();
  if (!name) return false;
  // Phase 1.8: parsing tollerante. intolRaw può essere "pesce", "no pesce",
  // "intolleranza al pesce", "no pesce — preferisce varietà" (testo libero
  // emesso da Sonnet). Cerchiamo se la stringa CONTIENE una keyword nota tra
  // le chiavi di INTOL_KEYWORDS_COMPOSER. Lookup esatto era troppo fragile.
  const intolText = String(intolRaw || '').toLowerCase().trim();
  if (!intolText) return false;
  for (const key of Object.keys(INTOL_KEYWORDS_COMPOSER)) {
    if (!intolText.includes(key)) continue;
    // Phase 5b: espandi via alias semantici (es. pesce → +crostacei+molluschi).
    const expandedKeys = INTOL_SEMANTIC_ALIAS[key] || [key];
    for (const expKey of expandedKeys) {
      const keywords = INTOL_KEYWORDS_COMPOSER[expKey];
      if (!keywords) continue;
      const isDairyIntol = expKey === 'lattosio' || expKey === 'latticini' || expKey === 'latte';
      if (isDairyIntol && DAIRY_SAFE_PATTERN.test(name)) continue;
      for (const kw of keywords) {
        if (name.includes(kw)) return true;
      }
    }
  }
  return false;
}

function recipeContainsIntoleranceViolation(recipe, profileIntol) {
  if (!profileIntol || profileIntol.length === 0) return false;
  const ings = recipe.ingredients_jsonb || [];
  for (const ing of ings) {
    for (const intol of profileIntol) {
      const intolKey = String(intol).toLowerCase().trim();
      if (ingredientViolatesIntolerance(ing, intolKey)) return true;
    }
  }
  return false;
}

// Phase 1.9/1.11: scan ingredienti per preferenze di gusto (es. "pane", "olive",
// "broccoli"). Diversamente da INTOL_KEYWORDS_COMPOSER (categorie macro), qui
// matchiamo direttamente il valore della preferenza come substring del canonical
// name. Es. preferenze.no=['pane'] → escludi qualsiasi ingrediente con "pane"
// nel nome (pane integrale, pane comune, pangrattato).
function recipeViolatesPreference(recipe, preferenzeNo) {
  if (!preferenzeNo || preferenzeNo.length === 0) return false;
  const ings = recipe.ingredients_jsonb || recipe.ingredients || [];
  for (const ing of ings) {
    const name = (ing.canonical_name || '').toLowerCase();
    if (!name) continue;
    for (const pref of preferenzeNo) {
      const prefText = String(pref || '').toLowerCase().trim();
      if (!prefText || prefText.length < 3) continue;  // skip short/empty
      // Match diretto: nome ingrediente contiene la preferenza
      if (name.includes(prefText)) return true;
      // Match via INTOL_KEYWORDS se la preferenza è una categoria macro
      // (riusa la stessa logica di ingredientViolatesIntolerance)
      if (ingredientViolatesIntolerance(ing, prefText)) return true;
    }
  }
  return false;
}

// Phase 1.11: una ricetta contiene `alimento`? Returns 1 (yes) o 0 (no).
// Logica match identica a recipeViolatesPreference (substring canonical_name +
// INTOL_KEYWORDS lookup per categorie macro). Binary 0/1 perché una ricetta
// è UN pasto, non importa quante volte l'alimento appare negli ingredienti.
function recipeContainsAlimento(recipe, alimento) {
  const al = String(alimento || '').toLowerCase().trim();
  if (!al || al.length < 3) return 0;
  const ings = recipe.ingredients_jsonb || recipe.ingredients || [];
  for (const ing of ings) {
    const name = (ing.canonical_name || '').toLowerCase();
    if (!name) continue;
    if (name.includes(al)) return 1;
    if (ingredientViolatesIntolerance(ing, al)) return 1;
  }
  return 0;
}

// Phase 5.9b (ADR-0018): rate limit per cultural_origin "esotico"/aggregate
// (BBC, MedDiet, themediterraneandish, feastingathome). Volume alto + non
// chef-specific = rischio sbilanciare piano se compaiono troppo spesso.
// Hardcoded cap: max 1 ricetta esotica per settimana. Altre origin: unlimited
// (chef-specific, autenticità per costruzione = OK ripetere).
const MAX_PER_WEEK_BY_ORIGIN = {
  mediterraneo_esotico: 1,
};

// Phase 5b (Meal Data Quality Overhaul): regole baseline di sana alimentazione
// applicate come SOFT PENALTY (riducono probability di pick, non bloccano).
//
// Le regole, con le rispettive fonti ufficiali (CREA LGSA 2018, WHO IARC 2015,
// piramide Mediterranean Diet Foundation, EFSA DRV), vivono in method-facts.js:
// sono le stesse mostrate all'utente sulla pagina /metodo, quindi hanno una
// fonte sola e non possono divergere dal testo pubblico.
//
// Sono OVERRIDE-BILI dal profilo utente (profile.preferenze.limita) per casi
// specifici (atleta, body recomp, post-op, ecc.). Override sono HARD (bloccano)
// via ConstraintTracker.canAdd(); le baseline sono SOFT (penalty in pick weight).

// Phase 1.11: ConstraintTracker per regole di frequenza (max_per_giorno,
// max_per_settimana, min_per_settimana). Usato sequenzialmente dentro
// composeMealSkeleton: per ogni slot pick si verifica canAdd → se OK chiama
// add per incrementare counter. Stato instance — uno per piano settimanale.
// Phase 5.9b: esteso con originWeekly counter per applicare MAX_PER_WEEK_BY_ORIGIN.
// Phase 5b (Meal Data Quality Overhaul): aggiunto softPenaltyFor() per applicare
// BASELINE_NUTRITIONAL_RULES come penalty weight (no blocco).
export class ConstraintTracker {
  constructor(preferenze) {
    const safe = preferenze && typeof preferenze === 'object' ? preferenze : {};
    this.limita = Array.isArray(safe.limita) ? safe.limita : [];
    this.preferenzeNo = Array.isArray(safe.no) ? safe.no : [];
    this.daily = {};         // {dayIdx: {alimento: count}}
    this.weekly = {};        // {alimento: count}
    this.originWeekly = {};  // Phase 5.9b: {cultural_origin: count}
    // Phase 5b F9: persistent history cross-generation. Array di
    // { variety_key, protein_name, carbo_name, days_ago } per ricette
    // scelte dall'utente nelle ultime 4 settimane.
    this.persistentHistory = [];
  }

  // Phase 5b F9: setter post-construction (history fetch è async)
  setPersistentHistory(historyRows) {
    this.persistentHistory = Array.isArray(historyRows) ? historyRows : [];
  }

  // Returns true se aggiungere `recipe` al `dayIdx` NON viola alcuna max regola.
  canAdd(recipe, dayIdx) {
    // Phase 5.9b: check origin rate limit prima delle alimento rules.
    const origin = recipe && recipe.cultural_origin;
    if (origin && MAX_PER_WEEK_BY_ORIGIN[origin] != null) {
      const oc = this.originWeekly[origin] || 0;
      if ((oc + 1) > MAX_PER_WEEK_BY_ORIGIN[origin]) return false;
    }
    for (const rule of this.limita) {
      if (!rule || !rule.alimento || !rule.regola) continue;
      const presence = recipeContainsAlimento(recipe, rule.alimento);
      if (presence === 0) continue;
      if (rule.regola === 'max_per_giorno') {
        const dc = this.daily[dayIdx]?.[rule.alimento] || 0;
        if ((dc + presence) > (rule.valore || 0)) return false;
      } else if (rule.regola === 'max_per_settimana') {
        const wc = this.weekly[rule.alimento] || 0;
        if ((wc + presence) > (rule.valore || 0)) return false;
      }
      // min_per_settimana NON è una regola che blocca: gestita post-hoc
      // come "soft hint" durante pick (preferisce ricette con l'alimento
      // se min non ancora raggiunto). Implementazione: bonus weight in
      // pickRecipeForSlot.
    }
    return true;
  }

  add(recipe, dayIdx) {
    // Phase 5.9b: track cultural_origin se presente nella recipe (pattern
    // hardcoded fallback breakfast/snack passano fakeRecipe senza origin =
    // skip silently).
    const origin = recipe && recipe.cultural_origin;
    if (origin) {
      this.originWeekly[origin] = (this.originWeekly[origin] || 0) + 1;
    }
    // Track sia regole utente (this.limita) sia baseline nutrizionali. Stessi
    // counter daily/weekly perché per chiave alimento sono semanticamente
    // equivalenti (count di presenza in ricette/giorno o settimana).
    const allTrackedAlimenti = new Set([
      ...this.limita.filter(r => r && r.alimento).map(r => r.alimento),
      ...BASELINE_NUTRITIONAL_RULES.map(r => r.alimento)
    ]);
    for (const alimento of allTrackedAlimenti) {
      const presence = recipeContainsAlimento(recipe, alimento);
      if (presence === 0) continue;
      if (!this.daily[dayIdx]) this.daily[dayIdx] = {};
      this.daily[dayIdx][alimento] = (this.daily[dayIdx][alimento] || 0) + presence;
      this.weekly[alimento] = (this.weekly[alimento] || 0) + presence;
    }
  }

  // Phase 1.11: per min_per_settimana, ritorna alimenti che non hanno ancora
  // raggiunto il minimo. Usato in pickRecipeForSlot come bonus weight.
  // Phase 5b: include anche BASELINE_NUTRITIONAL_RULES con regola='min_per_settimana'
  // (es. pesce ≥2/sett, legumi ≥2/sett), saltando quelli esclusi via preferenze.no.
  underMinimumAlimenti() {
    const under = [];
    const allMinRules = [
      ...this.limita.filter(r => r && r.regola === 'min_per_settimana'),
      ...BASELINE_NUTRITIONAL_RULES.filter(r => r.regola === 'min_per_settimana')
    ];
    for (const rule of allMinRules) {
      // Skip se l'alimento è escluso da preferenze.no (es. utente che dice "no pesce"
      // → niente bonus pesce sotto-minimo, sarebbe contro-richiesta utente)
      const excluded = this.preferenzeNo.some(p => {
        const pText = String(p || '').toLowerCase();
        return pText.includes(rule.alimento) || rule.alimento.includes(pText);
      });
      if (excluded) continue;
      const current = this.weekly[rule.alimento] || 0;
      if (current < (rule.valore || 0)) {
        under.push({ alimento: rule.alimento, deficit: (rule.valore || 0) - current });
      }
    }
    return under;
  }

  // Aderenza effettiva alle regole baseline a fine composizione della settimana.
  // Non serve al pick: e' il consuntivo che il piano mostra all'utente
  // ("pesce 2/2", "carne rossa 1/2") nel pannello "Perche' questo piatto".
  // Le regole su alimenti che l'utente ha escluso restano in lista ma marcate
  // `escluso`, cosi' l'UI puo' dire "non applicabile" invece di mostrare un
  // minimo mancato che sarebbe una contro-richiesta.
  weeklyRuleSummary() {
    return BASELINE_NUTRITIONAL_RULES.map(rule => {
      const escluso = this.preferenzeNo.some(p => {
        const pText = String(p || '').toLowerCase();
        return pText.includes(rule.alimento) || rule.alimento.includes(pText);
      });
      let actual;
      if (rule.regola === 'max_per_giorno') {
        actual = Object.values(this.daily).reduce(
          (max, day) => Math.max(max, day[rule.alimento] || 0), 0
        );
      } else {
        actual = this.weekly[rule.alimento] || 0;
      }
      const ok = rule.regola === 'min_per_settimana'
        ? actual >= rule.valore
        : actual <= rule.valore;
      return {
        alimento: rule.alimento,
        label: rule.label,
        regola: rule.regola,
        valore: rule.valore,
        actual,
        ok: escluso ? true : ok,
        escluso,
        source: rule.source
      };
    });
  }

  // Phase 5b (Meal Data Quality Overhaul): soft penalty per BASELINE_NUTRITIONAL_RULES.
  // NON blocca (canAdd resta come prima), ma riduce drasticamente il weight in pick.
  // - max_per_giorno violato → ×0.15 (forte deprioritizzazione)
  // - max_per_settimana violato → ×0.25
  // Returns un fattore [0, 1] da moltiplicare al weight della candidate.
  softPenaltyFor(recipe, dayIdx) {
    let penalty = 1.0;
    for (const rule of BASELINE_NUTRITIONAL_RULES) {
      if (rule.regola !== 'max_per_giorno' && rule.regola !== 'max_per_settimana') continue;
      // Skip se l'alimento è escluso da preferenze.no (irrilevante penalizzare
      // qualcosa che già non può apparire)
      const excluded = this.preferenzeNo.some(p => {
        const pText = String(p || '').toLowerCase();
        return pText.includes(rule.alimento) || rule.alimento.includes(pText);
      });
      if (excluded) continue;
      const presence = recipeContainsAlimento(recipe, rule.alimento);
      if (presence === 0) continue;
      if (rule.regola === 'max_per_giorno') {
        const dc = this.daily[dayIdx]?.[rule.alimento] || 0;
        if ((dc + presence) > (rule.valore || 0)) penalty *= 0.15;
      } else if (rule.regola === 'max_per_settimana') {
        const wc = this.weekly[rule.alimento] || 0;
        if ((wc + presence) > (rule.valore || 0)) penalty *= 0.25;
      }
    }
    return penalty;
  }

  // Phase 5b F9: persistent history cross-generation. Penalty progressiva per
  // ricette già scelte dall'utente nelle ultime 4 settimane. Risolve "Curry di
  // ceci esce ad ogni rigenerazione" — history era resettato a [] al composer
  // init, ora viene letto da meal_recipe_history table.
  // - variety_key match in ≤7 giorni  → ×0.10 (deprio quasi totale)
  // - variety_key match in 8-14 giorni → ×0.25
  // - variety_key match in 15-28 giorni → ×0.50
  // - anchor protein/carbo match in ≤3 giorni → ×0.7 (più clemente del variety)
  persistentPenaltyFor(recipe) {
    if (!this.persistentHistory.length) return 1.0;
    const ings = recipe.ingredients_jsonb || recipe.ingredients || [];
    const rProtein = ings.find(i => i.role === 'anchor_protein')?.canonical_name;
    const rCarbo   = ings.find(i => i.role === 'anchor_carbo')?.canonical_name;
    let penalty = 1.0;
    for (const h of this.persistentHistory) {
      if (h.variety_key && h.variety_key === recipe.variety_key) {
        if (h.days_ago <= 7) penalty *= 0.10;
        else if (h.days_ago <= 14) penalty *= 0.25;
        else if (h.days_ago <= 28) penalty *= 0.50;
      } else {
        if (rProtein && h.protein_name === rProtein && h.days_ago <= 3) penalty *= 0.70;
        if (rCarbo && h.carbo_name === rCarbo && h.days_ago <= 3) penalty *= 0.70;
      }
    }
    return penalty;
  }
}

// Phase 5b F9: fetch persistent history dall'ultima settimana/4 settimane.
// Non blocking (try/catch): se la query fallisce, ritorna [] e composer
// procede senza penalty cross-generation (graceful degrade).
export async function fetchPersistentHistory(supabase, profileId, daysBack = 28) {
  if (!supabase || !profileId) return [];
  try {
    const sinceIso = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('meal_recipe_history')
      .select('variety_key, protein_name, carbo_name, used_at')
      .eq('profile_id', profileId)
      .gte('used_at', sinceIso)
      .order('used_at', { ascending: false })
      .limit(300);
    if (error || !data) return [];
    const now = Date.now();
    return data.map(h => ({
      variety_key: h.variety_key,
      protein_name: h.protein_name,
      carbo_name: h.carbo_name,
      days_ago: Math.max(0, Math.floor((now - new Date(h.used_at).getTime()) / (24 * 60 * 60 * 1000)))
    }));
  } catch (e) {
    console.warn('fetchPersistentHistory error (non-blocking):', e && e.message);
    return [];
  }
}

// Phase 5b F9: persist piano generato in meal_recipe_history. Chiamato post-
// composeMealSkeleton da genMeal. Non blocking: errors loggati ma non
// interrompono il flow (UI/save procede comunque).
export async function persistPlanToHistory(supabase, profileId, skeleton, generationId) {
  if (!supabase || !profileId || !skeleton || !skeleton.days) return;
  try {
    const rows = [];
    for (const day of skeleton.days) {
      if (!day || !Array.isArray(day.m)) continue;
      for (const meal of day.m) {
        if (!meal || !Array.isArray(meal.items)) continue;
        for (const item of meal.items) {
          if (!item || !item._variety_key) continue;
          rows.push({
            profile_id: profileId,
            meal_recipe_id: item._recipe_id || null,
            variety_key: item._variety_key,
            protein_name: item._protein_name || null,
            carbo_name: item._carbo_name || null,
            slot: meal.t || null,
            cultural_origin: item._cultural_origin || null,
            generation_id: generationId || null,
          });
        }
      }
    }
    if (rows.length === 0) return;
    const { error } = await supabase.from('meal_recipe_history').insert(rows);
    if (error) console.warn('persistPlanToHistory error (non-blocking):', error.message);
  } catch (e) {
    console.warn('persistPlanToHistory exception (non-blocking):', e && e.message);
  }
}

// Phase 1.6: map tempo_minuti → categoria preferenza utente
// Boundaries allineate al prompt onboarding (prompts.js): rapido <15min,
// medio 15-30min, elaborato 30+min.
const TIME_CATEGORY_BOUNDS = {
  rapido:    { min: 0,  max: 15 },
  medio:     { min: 16, max: 30 },
  elaborato: { min: 31, max: 9999 }
};

// Phase 1.10: ceiling logic. tempo_* sul profilo è il TETTO MASSIMO che
// l'utente vuole/può cucinare, non la categoria esatta.
// - rapido (ha poco tempo): SOLO ricette rapido
// - medio (tempo normale): include rapido + medio
// - elaborato (sempre tempo): include tutto
// Logica precedente (TIME_FALLBACK_ORDER con fallback if pool<8) era errata:
// un utente "elaborato" non rifiuta ricette rapide solo perché ha tempo.
const TIME_CEILING_INCLUDES = {
  rapido:    new Set(['rapido']),
  medio:     new Set(['rapido', 'medio']),
  elaborato: new Set(['rapido', 'medio', 'elaborato'])
};

function recipeTimeCategory(recipe) {
  const t = recipe.tempo_minuti;
  if (t == null) return null;  // unknown → non filtrabile
  if (t <= TIME_CATEGORY_BOUNDS.rapido.max) return 'rapido';
  if (t <= TIME_CATEGORY_BOUNDS.medio.max)  return 'medio';
  return 'elaborato';
}

// Profile preference for slot (col/sna/pra/cen) → mapping del campo profile.tempo_*
function preferredTimeForSlot(profile, slot) {
  if (!profile) return null;
  if (slot === 'col' || slot === 'sna1' || slot === 'sna2') return profile.tempo_col_sna || null;
  if (slot === 'pra') return profile.tempo_pra || null;
  if (slot === 'cen') return profile.tempo_cen || null;
  return null;
}

// Helper: anchor_carbo è main-meal carb? (per filtrare snack-mistaggati)
function hasSnackForbiddenCarb(ingredients) {
  return (ingredients || []).some(i => {
    if (i.role !== 'anchor_carbo') return false;
    const n = (i.canonical_name || '').toLowerCase();
    return SNACK_BLACKLIST_CARBS.some(p => n.includes(p));
  });
}

// Helper: il nome contiene frittata/omelette? (anche se ingredienti non hanno)
function isSnackDishMain(recipe) {
  const nome = (recipe.nome_originale || '').toLowerCase();
  return SNACK_BLACKLIST_DISH_TERMS.some(t => nome.includes(t));
}

// ─── BREAKFAST PATTERNS — fallback colazione IT classico ───────────────
// 10 pattern hardcoded perché slot=colazione in DB ha solo 8 ricette,
// insufficienti per varietà 7 giorni × profili diversi. Riferiscono
// canonical_name di public.foods (verificati: yogurt greco, granola,
// avena fiocchi, pane integrale, ricotta, miele, mirtilli, fragole,
// banane, biscotti integrali, mandorle, noci, latte, uova).
//
// Shape: parallela a meal_recipes.ingredients_jsonb per riuso composeFromRecipe.
// role: anchor_protein | anchor_carbo | aromatic | fat
export const BREAKFAST_PATTERNS = [
  {
    nome: 'Yogurt greco con frutti di bosco e granola',
    variety_key: 'col__yogurt_greco__granola',
    base_kcal: 350,
    ingredients: [
      { canonical_name: 'Yogurt greco intero', qty_g: 170, role: 'anchor_protein' },
      { canonical_name: 'Granola naturale',    qty_g: 30,  role: 'anchor_carbo' },
      { canonical_name: 'Mirtilli',            qty_g: 60,  role: 'aromatic' },
      { canonical_name: 'Miele',               qty_g: 10,  role: 'fat' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele','frutta secca','frutta_secca']
  },
  {
    nome: 'Yogurt greco con miele e fragole',
    variety_key: 'col__yogurt_greco__fragole',
    base_kcal: 280,
    ingredients: [
      { canonical_name: 'Yogurt greco intero', qty_g: 200, role: 'anchor_protein' },
      { canonical_name: 'Miele',               qty_g: 15,  role: 'anchor_carbo' },
      { canonical_name: 'Fragole',             qty_g: 100, role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','miele']
  },
  {
    nome: 'Porridge di avena con latte e miele',
    variety_key: 'col__avena__latte',
    base_kcal: 380,
    ingredients: [
      { canonical_name: 'Avena fiocchi',           qty_g: 50,  role: 'anchor_carbo' },
      { canonical_name: 'Latte parzialmente scremato', qty_g: 200, role: 'anchor_protein' },
      { canonical_name: 'Miele',                   qty_g: 10,  role: 'fat' },
      { canonical_name: 'Mirtilli',                qty_g: 50,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele']
  },
  {
    nome: 'Pane integrale con ricotta e miele',
    variety_key: 'col__pane_integrale__ricotta',
    base_kcal: 350,
    ingredients: [
      { canonical_name: 'Pane integrale',  qty_g: 70,  role: 'anchor_carbo' },
      { canonical_name: 'Ricotta di vacca', qty_g: 100, role: 'anchor_protein' },
      { canonical_name: 'Miele',           qty_g: 10,  role: 'fat' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele']
  },
  {
    nome: 'Pane integrale con uova strapazzate',
    variety_key: 'col__pane_integrale__uova',
    base_kcal: 380,
    ingredients: [
      { canonical_name: 'Pane integrale', qty_g: 60,  role: 'anchor_carbo' },
      { canonical_name: 'Uova intere',    qty_g: 100, role: 'anchor_protein' },
      { canonical_name: 'Fragole',        qty_g: 80,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano','vegetariano'],
    excluded_intolleranze: ['uova','glutine']
  },
  {
    nome: 'Biscotti integrali con latte e frutta',
    variety_key: 'col__biscotti_integrali__latte',
    base_kcal: 360,
    ingredients: [
      { canonical_name: 'Biscotti integrali',      qty_g: 50,  role: 'anchor_carbo' },
      { canonical_name: 'Latte parzialmente scremato', qty_g: 200, role: 'anchor_protein' },
      { canonical_name: 'Mirtilli',                qty_g: 60,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine']
  },
  {
    nome: 'Yogurt di soia con granola e fragole',
    variety_key: 'col__yogurt_soia__granola',
    base_kcal: 330,
    ingredients: [
      { canonical_name: 'Yogurt di soia',   qty_g: 170, role: 'anchor_protein' },
      { canonical_name: 'Granola naturale', qty_g: 30,  role: 'anchor_carbo' },
      { canonical_name: 'Fragole',          qty_g: 80,  role: 'aromatic' },
      { canonical_name: 'Miele',            qty_g: 10,  role: 'fat' }
    ],
    excluded_styles: [],
    excluded_intolleranze: ['soia','glutine','miele','frutta secca','frutta_secca']
  },
  {
    nome: 'Avena con yogurt greco e noci',
    variety_key: 'col__avena__yogurt_noci',
    base_kcal: 400,
    ingredients: [
      { canonical_name: 'Avena fiocchi',       qty_g: 40,  role: 'anchor_carbo' },
      { canonical_name: 'Yogurt greco intero', qty_g: 150, role: 'anchor_protein' },
      { canonical_name: 'Noci',                qty_g: 15,  role: 'fat' },
      { canonical_name: 'Miele',               qty_g: 10,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele','frutta secca','frutta_secca']
  },
  {
    nome: 'Ricotta con miele e mandorle',
    variety_key: 'col__ricotta__mandorle',
    base_kcal: 320,
    ingredients: [
      { canonical_name: 'Ricotta di vacca', qty_g: 150, role: 'anchor_protein' },
      { canonical_name: 'Pane integrale',   qty_g: 50,  role: 'anchor_carbo' },
      { canonical_name: 'Mandorle',         qty_g: 10,  role: 'fat' },
      { canonical_name: 'Miele',            qty_g: 10,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele','frutta secca','frutta_secca']
  },
  {
    nome: 'Albume con avena e mirtilli',
    variety_key: 'col__albume__avena',
    base_kcal: 300,
    ingredients: [
      { canonical_name: "Albume d'uovo", qty_g: 120, role: 'anchor_protein' },
      { canonical_name: 'Avena fiocchi', qty_g: 45,  role: 'anchor_carbo' },
      { canonical_name: 'Mirtilli',      qty_g: 80,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano','vegetariano'],
    excluded_intolleranze: ['uova','glutine']
  },
  {
    nome: 'Smoothie banana yogurt e cioccolato fondente',
    variety_key: 'col__smoothie__banana_cioccolato',
    base_kcal: 380,
    ingredients: [
      { canonical_name: 'Banane',                      qty_g: 130, role: 'anchor_carbo' },
      { canonical_name: 'Yogurt greco intero',         qty_g: 150, role: 'anchor_protein' },
      { canonical_name: 'Latte parzialmente scremato', qty_g: 100, role: 'aromatic' },
      { canonical_name: 'Cioccolato fondente 70%+',    qty_g: 15,  role: 'fat' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini']
  },
  {
    nome: 'Toast con uova avocado e pomodorini',
    variety_key: 'col__toast__uova_avocado',
    base_kcal: 420,
    ingredients: [
      { canonical_name: 'Pane integrale', qty_g: 60,  role: 'anchor_carbo' },
      { canonical_name: 'Uova intere',    qty_g: 100, role: 'anchor_protein' },
      { canonical_name: 'Avocado',        qty_g: 50,  role: 'fat' },
      { canonical_name: 'Pomodorini',     qty_g: 70,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano','vegetariano'],
    excluded_intolleranze: ['uova','glutine']
  }
];

// ─── SNACK PATTERNS — Phase 1.5 fallback spuntino IT healthy ───────────
// 12 pattern italiani classici per slot=sna1/sna2. Risolvono il problema
// dello slot='spuntino' male-taggato in DB (frittate/pasta/wurstel finivano
// come spuntino). Pattern usati PRIMA del DB recipe pick; DB filtered è il
// fallback per profili rari.
//
// Range kcal target: 150-260 (slot ratio 0.10 × 2000-2600 kcal/d).
export const SNACK_PATTERNS = [
  {
    nome: 'Yogurt greco con frutti rossi e mandorle',
    variety_key: 'sna__yogurt__mandorle',
    base_kcal: 220,
    ingredients: [
      { canonical_name: 'Yogurt greco intero', qty_g: 150, role: 'anchor_protein' },
      { canonical_name: 'Mirtilli',            qty_g: 60,  role: 'aromatic' },
      { canonical_name: 'Mandorle',            qty_g: 10,  role: 'fat' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','frutta secca','frutta_secca']
  },
  {
    nome: 'Mela con burro di mandorle',
    variety_key: 'sna__mela__burro_mandorle',
    base_kcal: 180,
    ingredients: [
      { canonical_name: 'Mele',              qty_g: 150, role: 'anchor_carbo' },
      { canonical_name: 'Burro di mandorle', qty_g: 15,  role: 'anchor_protein' }
    ],
    excluded_styles: [],
    excluded_intolleranze: ['frutta secca','frutta_secca']
  },
  {
    nome: 'Hummus con verdure e crackers integrali',
    variety_key: 'sna__hummus__verdure',
    base_kcal: 200,
    ingredients: [
      { canonical_name: 'Ceci',               qty_g: 60,  role: 'anchor_protein' },
      { canonical_name: 'Carote',             qty_g: 80,  role: 'aromatic' },
      { canonical_name: 'Cracker integrali',  qty_g: 20,  role: 'anchor_carbo' },
      { canonical_name: 'Olio EVO (extra vergine d\'oliva)', qty_g: 5, role: 'fat' }
    ],
    excluded_styles: [],
    excluded_intolleranze: ['glutine']
  },
  {
    nome: 'Pane integrale con ricotta e miele',
    variety_key: 'sna__pane__ricotta_miele',
    base_kcal: 210,
    ingredients: [
      { canonical_name: 'Pane integrale',  qty_g: 40,  role: 'anchor_carbo' },
      { canonical_name: 'Ricotta di vacca', qty_g: 60,  role: 'anchor_protein' },
      { canonical_name: 'Miele',           qty_g: 8,   role: 'fat' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele']
  },
  {
    nome: 'Banana con yogurt e cannella',
    variety_key: 'sna__banana__yogurt',
    base_kcal: 200,
    ingredients: [
      { canonical_name: 'Banane',              qty_g: 120, role: 'anchor_carbo' },
      { canonical_name: 'Yogurt greco intero', qty_g: 100, role: 'anchor_protein' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini']
  },
  {
    nome: 'Pera con parmigiano',
    variety_key: 'sna__pera__parmigiano',
    base_kcal: 180,
    ingredients: [
      { canonical_name: 'Pere',       qty_g: 150, role: 'anchor_carbo' },
      { canonical_name: 'Parmigiano', qty_g: 25,  role: 'anchor_protein' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini']
  },
  {
    nome: 'Mix di frutta secca e frutta fresca',
    variety_key: 'sna__frutta_secca_fresca',
    base_kcal: 200,
    ingredients: [
      { canonical_name: 'Mandorle',  qty_g: 15,  role: 'anchor_protein' },
      { canonical_name: 'Noci',      qty_g: 10,  role: 'fat' },
      { canonical_name: 'Mele',      qty_g: 120, role: 'anchor_carbo' }
    ],
    excluded_styles: [],
    excluded_intolleranze: ['frutta secca','frutta_secca']
  },
  {
    nome: 'Pane integrale con miele e yogurt',
    variety_key: 'sna__pane__miele_yogurt',
    base_kcal: 190,
    ingredients: [
      { canonical_name: 'Pane integrale',          qty_g: 35,  role: 'anchor_carbo' },
      { canonical_name: 'Miele',                   qty_g: 12,  role: 'aromatic' },
      { canonical_name: 'Yogurt naturale magro',   qty_g: 80,  role: 'anchor_protein' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','miele']
  },
  {
    nome: 'Yogurt greco con granola e frutta',
    variety_key: 'sna__yogurt__granola',
    base_kcal: 230,
    ingredients: [
      { canonical_name: 'Yogurt greco intero', qty_g: 130, role: 'anchor_protein' },
      { canonical_name: 'Granola naturale',    qty_g: 20,  role: 'anchor_carbo' },
      { canonical_name: 'Fragole',             qty_g: 70,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine','frutta secca','frutta_secca']
  },
  {
    nome: 'Cracker integrali con ricotta e pomodorini',
    variety_key: 'sna__cracker__ricotta_pomodorini',
    base_kcal: 200,
    ingredients: [
      { canonical_name: 'Cracker integrali', qty_g: 25,  role: 'anchor_carbo' },
      { canonical_name: 'Ricotta di vacca',  qty_g: 60,  role: 'anchor_protein' },
      { canonical_name: 'Pomodorini',        qty_g: 80,  role: 'aromatic' }
    ],
    excluded_styles: ['vegano'],
    excluded_intolleranze: ['lattosio','latticini','glutine']
  },
  {
    nome: 'Pane integrale con bresaola e rucola',
    variety_key: 'sna__pane__bresaola_rucola',
    base_kcal: 220,
    ingredients: [
      { canonical_name: 'Pane integrale', qty_g: 50,  role: 'anchor_carbo' },
      { canonical_name: 'Bresaola',       qty_g: 50,  role: 'anchor_protein' },
      { canonical_name: 'Rucola',         qty_g: 30,  role: 'aromatic' },
      { canonical_name: 'Olio EVO (extra vergine d\'oliva)', qty_g: 4, role: 'fat' }
    ],
    excluded_styles: ['vegano','vegetariano','pescetariano'],
    excluded_intolleranze: ['carne','glutine']
  },
  {
    nome: 'Yogurt di soia con fragole e semi',
    variety_key: 'sna__yogurt_soia__fragole',
    base_kcal: 180,
    ingredients: [
      { canonical_name: 'Yogurt di soia',  qty_g: 150, role: 'anchor_protein' },
      { canonical_name: 'Fragole',         qty_g: 100, role: 'aromatic' },
      { canonical_name: 'Semi di chia',    qty_g: 8,   role: 'fat' }
    ],
    excluded_styles: [],
    excluded_intolleranze: ['soia']
  }
];

// Phase 1.8: profileHasIntol controlla se UNO degli elementi di profileIntol
// (testo libero emesso da Sonnet) CONTIENE la keyword canonical dell'exclude.
// Risolve bug "intolleranze: ['no pesce — preferisce varietà']" → 'pesce' key.
function profileHasIntol(profileIntol, intolKey) {
  const target = String(intolKey || '').toLowerCase();
  if (!target) return false;
  return (profileIntol || []).some(raw =>
    String(raw || '').toLowerCase().includes(target)
  );
}

// ─── Filtro compatibilità (DB recipes + breakfast patterns) ─────────────
// Phase 1.7/1.8/1.9: defense in depth.
// 1. excluded_styles/excluded_intolleranze DB check (con parsing tollerante 1.8)
// 2. client-side scan ingredients per intolleranze mediche
// 3. client-side scan ingredients per preferenze_no di gusto (1.9)
function isRecipeCompatible(recipe, profileStyles, profileIntol, profilePreferenzeNo) {
  const exclS = recipe.excluded_styles || [];
  const exclI = recipe.excluded_intolleranze || [];
  if (exclS.some(s => profileStyles.includes(s))) return false;
  if (exclI.some(intolKey => profileHasIntol(profileIntol, intolKey))) return false;
  if (recipeContainsIntoleranceViolation(recipe, profileIntol)) return false;
  if (recipeViolatesPreference(recipe, profilePreferenzeNo)) return false;
  return true;
}

// Phase 1.5/1.7: filtro slot-specifico. Per spuntini: no main-meal carbs +
// no frittate/omelette. Per tutti gli slot: kcal range proporzionale al
// kcalTarget dell'utente (passato da pickRecipeForSlot).
function isRecipeForSlot(recipe, slot, slotKcalTarget) {
  const recipeK = parseFloat(recipe.total_kcal_estimated) || 0;
  const range = slotKcalRange(slot, slotKcalTarget);
  if (range && (recipeK < range.min || recipeK > range.max)) return false;
  if (slot === 'sna1' || slot === 'sna2') {
    if (hasSnackForbiddenCarb(recipe.ingredients_jsonb)) return false;
    if (isSnackDishMain(recipe)) return false;
  }
  return true;
}

// ─── Loader ricette compatibili (cache module-scope) ────────────────────
export async function preloadCompatibleRecipes(supabase, profile) {
  const profileStyles = profile.styles || [];
  // BKL-039: allergeni EU 14 mergiati con intolleranze per hard-block identico
  const profileIntol  = [...(profile.intolleranze || []), ...(profile.allergeni || [])];
  // Phase 1.11: profile.preferenze.no preferito, fallback su preferenze_no legacy
  const profilePrefNo = (profile.preferenze && Array.isArray(profile.preferenze.no))
    ? profile.preferenze.no
    : (profile.preferenze_no || []);
  // Phase 5 (ADR-0018): filtro cultural_origin via preferenze.cucine_attive.
  // Se assente/null/[]/non-array → no filter = tutte le origin attive (backward-
  // compat con profili pre-Fase 5).
  const cucineAttive = (profile.preferenze && Array.isArray(profile.preferenze.cucine_attive))
    ? profile.preferenze.cucine_attive
    : null;
  const hasOriginFilter = Array.isArray(cucineAttive) && cucineAttive.length > 0;

  const key = JSON.stringify({ s: profileStyles, i: profileIntol, p: profilePrefNo, c: hasOriginFilter ? cucineAttive : null });
  if (_cacheKey === key && _recipesCache) return _recipesCache;

  // BKL-007 fix 2026-05-15: PostgREST hard limit 1000 rows per response
  // (.range() cappato server-side, max-rows config). Paginazione client-side
  // necessaria per pool > 1000 records (oggi 3621).
  const PAGE_SIZE = 1000;
  const allRows = [];
  let from = 0;
  while (true) {
    let q = supabase
      .from('meal_recipes')
      .select('id, nome_originale, slot, ingredients_jsonb, total_kcal_estimated, total_p_g, total_c_g, total_g_g, excluded_styles, excluded_intolleranze, variety_key, cultural_origin, tempo_minuti')
      .eq('is_active', true)
      .range(from, from + PAGE_SIZE - 1);
    if (hasOriginFilter) q = q.in('cultural_origin', cucineAttive);
    const { data, error } = await q;
    if (error) throw new Error('preloadCompatibleRecipes: ' + error.message);
    if (!data || data.length === 0) break;
    allRows.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
    if (from > 50000) break; // safety cap
  }

  const filtered = allRows.filter(r => isRecipeCompatible(r, profileStyles, profileIntol, profilePrefNo));
  _recipesCache = filtered;
  _cacheKey = key;
  return filtered;
}

// ─── Weighted random pick per slot ──────────────────────────────────────
// Peso = variety_penalty × kcal_proximity_bonus
//   variety_penalty: 1 / (1 + count(variety_key in history))
//   kcal_proximity_bonus: clamp(1 - |kcal_recipe - kcal_target| / kcal_target, 0.3, 1)
export function pickRecipeForSlot(recipes, slot, kcalTarget, history, profile, tracker, dayIdx, sameDayKeys) {
  const dbSlots = SLOT_TO_DB[slot] || [];
  // Phase 1.5/1.7: filter by slot match + slot-specific quality (kcal range
  // proporzionale al kcalTarget, no main-meal carbs for snacks, no frittate)
  let candidates = recipes.filter(r => dbSlots.includes(r.slot) && isRecipeForSlot(r, slot, kcalTarget));
  if (candidates.length === 0) return null;

  // Phase 1.11: filter ricette che violerebbero regole max_per_giorno/settimana
  if (tracker) {
    const filtered = candidates.filter(r => tracker.canAdd(r, dayIdx));
    if (filtered.length > 0) candidates = filtered;
    // Se vuoto (es. regole troppo restrittive) lascia originale per non
    // bloccare la generazione
  }

  // Phase 5b (F6): hard-block ricetta identica entro 2 giorni. Risolve
  // "Curry di ceci con riso basmati e mele 2 cene di fila" — la variety
  // penalty soft (×3) lasciava ancora 0.25 weight dopo 1 pick, ripickabile
  // se pool DB è grande. Per ricetta esatta (variety_key match) imponiamo
  // gap minimo di 2 giorni.
  const HARD_BLOCK_WINDOW_DAYS = 2;
  if (history.length > 0) {
    const recentExactKeys = new Set(
      history.slice(-HARD_BLOCK_WINDOW_DAYS).flatMap(h => [
        h.col_variety, h.sna1_variety, h.pra_variety, h.sna2_variety, h.cen_variety
      ].filter(Boolean))
    );
    if (recentExactKeys.size > 0) {
      const filteredHard = candidates.filter(r => !recentExactKeys.has(r.variety_key));
      if (filteredHard.length > 0) candidates = filteredHard;
      // Edge: pool < window — lascia originale per non crashare
    }
  }

  // Phase 1.10: filtra per ceiling tempo cottura.
  // tempo_* sul profilo è il MASSIMO accettato: rapido→solo rapide, medio→
  // rapide+medie, elaborato→tutte. Niente fallback artificiale: se l'utente
  // dice "rapido" il composer rispetta strict (eventuale graceful degrade solo
  // se pool=0 = filtro lasciato passare per evitare crash).
  const preferredTime = preferredTimeForSlot(profile, slot);
  if (preferredTime && TIME_CEILING_INCLUDES[preferredTime]) {
    const allowed = TIME_CEILING_INCLUDES[preferredTime];
    const filtered = candidates.filter(r => {
      const rc = recipeTimeCategory(r);
      return rc === null || allowed.has(rc);
    });
    if (filtered.length > 0) candidates = filtered;
    // Se filtered è vuoto (rarissimo, es. rapido+pra con 0 ricette ≤15min
    // compatibili per profilo restrittivo) lascia candidates originali per
    // non lasciare l'utente senza piano.
  }

  // Phase 5b: history estesa — include variety_key + anchor protein/carbo
  // di tutti gli slot. Risolve cross-day anchor repetition (es. ricotta-anchor
  // in col giorno 1 e col giorno 2 con variety_key diversi).
  const recentVariety = history.flatMap(h => [
    h.col_variety, h.sna1_variety, h.pra_variety, h.sna2_variety, h.cen_variety,
    h.col_protein, h.sna1_protein, h.pra_protein, h.sna2_protein, h.cen_protein,
    h.col_carbo,   h.sna1_carbo,   h.pra_carbo,   h.sna2_carbo,   h.cen_carbo
  ].filter(Boolean));
  // Same-day cross-check: items già pickati oggi (col/sna1/pra) per evitare
  // es. pera+parmigiano in sna1 E sna2 dello stesso giorno (problema F2).
  if (Array.isArray(sameDayKeys) && sameDayKeys.length) {
    recentVariety.push(...sameDayKeys.filter(Boolean));
  }

  // Phase 1.11: min_per_settimana bonus. Se ci sono alimenti sotto-minimo
  // (es. "pesce min 2 a settimana" e abbiamo 0 pesce finora), bonus weight
  // 2× per ricette che li contengono.
  const underMin = tracker ? tracker.underMinimumAlimenti() : [];

  const sameDaySet = new Set(Array.isArray(sameDayKeys) ? sameDayKeys : []);
  const weighted = candidates.map(r => {
    // Phase 5b (Meal Data Quality Overhaul): penalty ×2 allineata ai pattern.
    // Pre-fix era 1/(1+n) — troppo blanda per pool DB piccolo dopo filtri:
    // 1ª ripetizione weight=0.5, 4ª=0.2 → ancora pickabile (es. "Riso integrale
    // con miele" 5×/7 nel reportage utente). ×2 forza varietà reale.
    let varCount = recentVariety.filter(v => v === r.variety_key).length;
    // Phase 5b: penalty anche per anchor protein/carbo match same-day.
    // Risolve "Pane integrale con ricotta" col + "Cracker con ricotta" sna
    // stesso giorno (variety_key diversi ma stessa anchor protein).
    const ings = r.ingredients_jsonb || r.ingredients || [];
    const rProtein = ings.find(i => i.role === 'anchor_protein')?.canonical_name;
    const rCarbo   = ings.find(i => i.role === 'anchor_carbo')?.canonical_name;
    if (rProtein && sameDaySet.has(rProtein)) varCount++;
    if (rCarbo && sameDaySet.has(rCarbo)) varCount++;
    const varPenalty = 1 / (1 + varCount * 2);
    const recipeK = parseFloat(r.total_kcal_estimated) || 400;
    const kcalDist = Math.abs(recipeK - kcalTarget);
    const kcalBonus = Math.max(0.3, 1 - kcalDist / Math.max(kcalTarget, 1));
    let minBonus = 1;
    for (const u of underMin) {
      if (recipeContainsAlimento(r, u.alimento) > 0) minBonus *= 2;
    }
    // Phase 5b: soft penalty per BASELINE_NUTRITIONAL_RULES (CREA LGSA / WHO IARC)
    const baselinePenalty = tracker ? tracker.softPenaltyFor(r, dayIdx) : 1;
    // Phase 5b F9: persistent history penalty (ricette già viste ultime 4 sett.)
    const persistentPenalty = tracker ? tracker.persistentPenaltyFor(r) : 1;
    return { r, weight: varPenalty * kcalBonus * minBonus * baselinePenalty * persistentPenalty };
  });

  const totalW = weighted.reduce((s, x) => s + x.weight, 0);
  if (totalW <= 0) return candidates[Math.floor(Math.random() * candidates.length)];
  let r = Math.random() * totalW;
  for (const x of weighted) {
    r -= x.weight;
    if (r <= 0) return x.r;
  }
  return weighted[weighted.length - 1].r;
}

// ─── Pattern picker (breakfast + snack share logic) ──────────────────────
function pickPatternFromList(patterns, profile, history, kcalTarget, varietyExtractor, tracker, dayIdx, sameDayKeys) {
  const styles = profile.styles || [];
  const intol  = profile.intolleranze || [];
  // Phase 1.11: preferenze.no preferito, fallback su preferenze_no legacy.
  const prefNo = (profile.preferenze && Array.isArray(profile.preferenze.no))
    ? profile.preferenze.no
    : (profile.preferenze_no || []);
  const compatible = patterns.filter(p => {
    if (p.excluded_styles.some(s => styles.includes(s))) return false;
    if (p.excluded_intolleranze.some(i => intol.includes(i))) return false;
    const fakeRecipe = { ingredients_jsonb: p.ingredients };
    if (recipeContainsIntoleranceViolation(fakeRecipe, intol)) return false;
    if (recipeViolatesPreference(fakeRecipe, prefNo)) return false;
    // Phase 1.11: filter constraint tracking
    if (tracker && !tracker.canAdd(fakeRecipe, dayIdx)) return false;
    return true;
  });
  if (compatible.length === 0) return null;

  const recentVariety = history.flatMap(varietyExtractor).filter(Boolean);
  // Phase 5b: same-day cross-check (idem pickRecipeForSlot)
  const sameDaySet = new Set(Array.isArray(sameDayKeys) ? sameDayKeys.filter(Boolean) : []);
  if (sameDaySet.size) {
    recentVariety.push(...sameDaySet);
  }
  const weighted = compatible.map(p => {
    let varCount = recentVariety.filter(v => v === p.variety_key).length;
    // Phase 5b: penalty anche per anchor protein/carbo match same-day
    const pIngs = p.ingredients || [];
    const pProtein = pIngs.find(i => i.role === 'anchor_protein')?.canonical_name;
    const pCarbo   = pIngs.find(i => i.role === 'anchor_carbo')?.canonical_name;
    if (pProtein && sameDaySet.has(pProtein)) varCount++;
    if (pCarbo && sameDaySet.has(pCarbo)) varCount++;
    const varPenalty = 1 / (1 + varCount * 2);
    const kcalDist = Math.abs(p.base_kcal - kcalTarget);
    const kcalBonus = Math.max(0.4, 1 - kcalDist / Math.max(kcalTarget, 1));
    // Phase 5b: soft penalty per BASELINE_NUTRITIONAL_RULES anche per pattern.
    // I pattern hardcoded sono già curated healthy ma il check resta utile per
    // edge-case (es. 2 pattern uova-based stesso giorno).
    const patternRecipe = { ingredients_jsonb: p.ingredients, variety_key: p.variety_key };
    const baselinePenalty = tracker ? tracker.softPenaltyFor(patternRecipe, dayIdx) : 1;
    // Phase 5b F9: persistent history penalty anche per pattern hardcoded
    const persistentPenalty = tracker ? tracker.persistentPenaltyFor(patternRecipe) : 1;
    return { p, weight: varPenalty * kcalBonus * baselinePenalty * persistentPenalty };
  });
  const totalW = weighted.reduce((s, x) => s + x.weight, 0);
  if (totalW <= 0) return compatible[Math.floor(Math.random() * compatible.length)];
  let r = Math.random() * totalW;
  for (const x of weighted) {
    r -= x.weight;
    if (r <= 0) return x.p;
  }
  return weighted[weighted.length - 1].p;
}

function pickBreakfastPattern(profile, history, kcalTarget, tracker, dayIdx, sameDayKeys) {
  return pickPatternFromList(
    BREAKFAST_PATTERNS, profile, history, kcalTarget,
    h => [h.col_variety], tracker, dayIdx, sameDayKeys
  );
}

function pickSnackPattern(profile, history, kcalTarget, tracker, dayIdx, sameDayKeys) {
  return pickPatternFromList(
    SNACK_PATTERNS, profile, history, kcalTarget,
    h => [h.sna1_variety, h.sna2_variety], tracker, dayIdx, sameDayKeys
  );
}

// ─── Resolve foods da canonical_name usando foodCache (Map lowercase) ───
function resolveFood(canonicalName, foodCache) {
  if (!foodCache || !canonicalName) return null;
  return foodCache.get(canonicalName.toLowerCase()) || null;
}

// ─── Scaling logic: scala anchor qty per matching kcal target ──────────
// Restituisce: { scaledIngredients, totalK, totalP, totalC, totalF }
function scaleAndCompute(ingredients, baseKcal, kcalTarget, foodCache) {
  const anchors = ingredients.filter(i => i.role === 'anchor_protein' || i.role === 'anchor_carbo');
  const others  = ingredients.filter(i => i.role !== 'anchor_protein' && i.role !== 'anchor_carbo');

  const scaleFactor = baseKcal > 0 ? (kcalTarget / baseKcal) : 1;

  const scaledAnchors = anchors.map(i => {
    let newQty = Math.round((i.qty_g || 0) * scaleFactor);
    if (newQty < 40) newQty = 40;
    if (newQty > 300) newQty = 300;
    return { ...i, qty_g: newQty };
  });

  const allIng = [...scaledAnchors, ...others];

  // Recompute macros from foods table when possible (foodCache lookup);
  // fallback: usa kcal/macro ratio del food in ingredient stesso se disponibile
  let totalK = 0, totalP = 0, totalC = 0, totalF = 0;
  for (const ing of allIng) {
    const food = resolveFood(ing.canonical_name, foodCache);
    if (food) {
      const f = (ing.qty_g || 0) / 100;
      totalK += (food.kcal_per_100g || 0) * f;
      totalP += (food.proteine_g_per_100g || 0) * f;
      totalC += (food.carbo_g_per_100g || 0) * f;
      totalF += (food.grassi_g_per_100g || 0) * f;
    }
  }

  return { ingredients: allIng, totalK, totalP, totalC, totalF, scaleFactor, baseKcal };
}

// ─── Build item shape compatibile con UI ────────────────────────────────
// `kcalTarget` serve solo al blocco `why`: e' la quota di calorie assegnata
// allo slot, cioe' il numero su cui la ricetta viene riscalata.
function buildItem(recipe, slot, scaled, kcalTarget) {
  const anchorProtein = scaled.ingredients.find(i => i.role === 'anchor_protein');
  const anchorCarbo   = scaled.ingredients.find(i => i.role === 'anchor_carbo');
  const anchorFat     = scaled.ingredients.find(i => i.role === 'fat');

  return {
    pp: anchorProtein ? shortName(anchorProtein.canonical_name) : '',
    cc: anchorCarbo   ? shortName(anchorCarbo.canonical_name)   : '',
    gg: anchorFat     ? shortName(anchorFat.canonical_name)     : '',
    ig: scaled.ingredients.map(i => ({
      n: (i.canonical_name || '').toLowerCase(),
      q: i.qty_g,
      u: 'g'
    })),
    nm: '',  // riempito da haikuFillDetails
    sb: '',
    st: [],
    mc: {
      k: Math.round(scaled.totalK),
      p: scaled.totalP.toFixed(1) + 'g',
      c: scaled.totalC.toFixed(1) + 'g',
      f: scaled.totalF.toFixed(1) + 'g'
    },
    // Tracciabilita' mostrata all'utente nel pannello "Perche' questo piatto".
    // Niente underscore: deve sopravvivere alla serializzazione del piano.
    why: {
      slot: slot,
      slotKcal: kcalTarget != null ? Math.round(kcalTarget) : null,
      baseKcal: scaled.baseKcal != null ? Math.round(scaled.baseKcal) : null,
      origin: recipe.cultural_origin || null,
      tempo: recipe.tempo_minuti != null ? recipe.tempo_minuti : null,
      fonte: recipe._is_pattern ? 'pattern' : 'ricetta'
    },
    _recipe_id:   recipe._is_pattern ? null : (recipe.id || null),
    _recipe_name: recipe.nome_originale || recipe.nome || '',
    _variety_key: recipe.variety_key,
    _protein_name: anchorProtein ? anchorProtein.canonical_name : null,
    _carbo_name:   anchorCarbo   ? anchorCarbo.canonical_name   : null,
    _is_pattern: !!recipe._is_pattern
  };
}

// ─── Main entry: compose item per slot ─────────────────────────────────
export async function composeFromRecipe(supabase, slot, profile, history, kcalTarget, foodCache, tracker, dayIdx, sameDayItems) {
  // Phase 5b: extract fingerprint da items già scelti oggi.
  // Include variety_key + anchor protein/carbo canonical_name → consente
  // di evitare "ricotta col + ricotta sna" stesso giorno anche se i due
  // variety_key sono distinti (es. 'col__pane__ricotta' vs 'sna__cracker__
  // ricotta_pomodorini'): l'anchor protein 'Ricotta di vacca' è comune.
  const sameDayKeys = Array.isArray(sameDayItems)
    ? sameDayItems.flatMap(it => it
        ? [it._variety_key, it._protein_name, it._carbo_name].filter(Boolean)
        : []
      )
    : [];
  // Phase 1.5/1.12: slot=col e slot=sna1/sna2 → pattern hardcoded prima del DB.
  // Pattern sono più controllati per qualità/coerenza italiana.
  // Phase 1.12: se profile.tempo_col_sna='elaborato' su col, ribalto la priorità:
  // DB recipes (8 ricette colazione tutte elaborate, es. frittate/uova) prima,
  // fallback a pattern. Coerente con ceiling logic: chi ha tempo vuole anche
  // colazioni più complesse, non solo assemblaggio.
  if (slot === 'col') {
    const wantElaborate = (profile.tempo_col_sna === 'elaborato');
    if (wantElaborate) {
      // Try DB recipe first (8 colazione recipes are all elaborate by design)
      const recipes = await preloadCompatibleRecipes(supabase, profile);
      const recipe = pickRecipeForSlot(recipes, slot, kcalTarget, history, profile, tracker, dayIdx, sameDayKeys);
      if (recipe) {
        const baseKcal = parseFloat(recipe.total_kcal_estimated) || 400;
        const scaled = scaleAndCompute(recipe.ingredients_jsonb || [], baseKcal, kcalTarget, foodCache);
        if (tracker) tracker.add(recipe, dayIdx);
        return buildItem(recipe, slot, scaled, kcalTarget);
      }
      // Fallback: pattern hardcoded (se DB pool è insufficiente per profilo)
    }
    const pattern = pickBreakfastPattern(profile, history, kcalTarget, tracker, dayIdx, sameDayKeys);
    if (pattern) {
      const scaled = scaleAndCompute(pattern.ingredients, pattern.base_kcal, kcalTarget, foodCache);
      if (tracker) tracker.add({ ingredients_jsonb: pattern.ingredients }, dayIdx);
      return buildItem({
        _is_pattern: true,
        nome_originale: pattern.nome,
        variety_key: pattern.variety_key
      }, slot, scaled, kcalTarget);
    }
  } else if (slot === 'sna1' || slot === 'sna2') {
    const pattern = pickSnackPattern(profile, history, kcalTarget, tracker, dayIdx, sameDayKeys);
    if (pattern) {
      const scaled = scaleAndCompute(pattern.ingredients, pattern.base_kcal, kcalTarget, foodCache);
      if (tracker) tracker.add({ ingredients_jsonb: pattern.ingredients }, dayIdx);
      return buildItem({
        _is_pattern: true,
        nome_originale: pattern.nome,
        variety_key: pattern.variety_key
      }, slot, scaled, kcalTarget);
    }
  }

  const recipes = await preloadCompatibleRecipes(supabase, profile);
  const recipe = pickRecipeForSlot(recipes, slot, kcalTarget, history, profile, tracker, dayIdx, sameDayKeys);
  if (!recipe) {
    // Hard fallback per profili rari: ricerca compatibile ignorando slot
    // (mantiene blacklist processed meat, ma ignora kcal range/snack carbs)
    const looseDbSlots = ['pranzo','cena','pranzo_o_cena','pranzo_leggero','spuntino','colazione','contorno'];
    const fallback = recipes.find(r => looseDbSlots.includes(r.slot));
    if (!fallback) {
      throw new Error('composeFromRecipe: nessuna ricetta compatibile per slot=' + slot + ' (profile styles/intoll troppo restrittivi?)');
    }
    const scaled = scaleAndCompute(fallback.ingredients_jsonb || [], parseFloat(fallback.total_kcal_estimated) || 400, kcalTarget, foodCache);
    if (tracker) tracker.add(fallback, dayIdx);
    return buildItem(fallback, slot, scaled, kcalTarget);
  }

  const baseKcal = parseFloat(recipe.total_kcal_estimated) || 400;
  const scaled = scaleAndCompute(recipe.ingredients_jsonb || [], baseKcal, kcalTarget, foodCache);
  if (tracker) tracker.add(recipe, dayIdx);
  return buildItem(recipe, slot, scaled, kcalTarget);
}

// ─── BKL-030: Swap singola ricetta (pulsante 🔄 in drawer) ──────────────────
// Picks an alternative recipe for the given slot, excluding the current one.
// Returns a UI-compatible item, or null if no alternative is available.
// Uses stored aggregate macros (no foodCache) — sufficient for display.
// BKL-007: wrappato in try/catch — qualsiasi eccezione interna viene degradata
// a null ("Nessuna alternativa") invece di propagare come throw ("Errore"). Evita
// asimmetria UI tra slot col (null) e slot pra/cen/sna1/sna2 (exception).
export async function swapSingleRecipe(supabase, slot, profile, excludeRecipeId, slotKcalTarget) {
  try {
    const recipes = await preloadCompatibleRecipes(supabase, profile);
    const available = excludeRecipeId
      ? recipes.filter(r => String(r.id) !== String(excludeRecipeId))
      : recipes;
    // BKL-007 diagnostic logging: aiuta a capire perché slot fa "Nessuna alternativa"
    const dbSlotsForLog = SLOT_TO_DB[slot] || [];
    const slotMatching = available.filter(r => dbSlotsForLog.includes(r.slot));
    console.log('[swapSingleRecipe]', {
      slot, slotKcalTarget,
      poolTotal: recipes.length,
      poolAfterExclude: available.length,
      dbSlots: dbSlotsForLog,
      slotMatching: slotMatching.length
    });
    const recipe = pickRecipeForSlot(available, slot, slotKcalTarget || 400, [], profile, null, 0, []);
    if (!recipe) {
      console.warn('[swapSingleRecipe] pickRecipeForSlot returned null for slot=' + slot + ', slotMatching=' + slotMatching.length);
      return null;
    }
    const baseKcal = parseFloat(recipe.total_kcal_estimated) || slotKcalTarget || 400;
    const sf = (baseKcal > 0 && slotKcalTarget > 0) ? slotKcalTarget / baseKcal : 1;
    const ings = (recipe.ingredients_jsonb || []).map(i => ({
      n: i.canonical_name || '',
      q: `${Math.round((i.qty_g || 0) * sf)}g`,
    }));
    return {
      nm: recipe.nome_originale || '',
      sb: ings.slice(0, 3).map(i => i.n).join(' · '),
      ig: ings,
      st: [],
      mc: {
        k: Math.round((recipe.total_kcal_estimated || 0) * sf),
        p: ((recipe.total_p_g || 0) * sf).toFixed(1) + 'g',
        c: ((recipe.total_c_g || 0) * sf).toFixed(1) + 'g',
        f: ((recipe.total_g_g || 0) * sf).toFixed(1) + 'g',
      },
      // Stesso blocco di tracciabilita' delle ricette composte, cosi' il
      // pannello "Perche' questo piatto" resta popolato anche dopo uno swap.
      why: {
        slot: slot,
        slotKcal: slotKcalTarget ? Math.round(slotKcalTarget) : null,
        baseKcal: Math.round(baseKcal),
        origin: recipe.cultural_origin || null,
        tempo: recipe.tempo_minuti != null ? recipe.tempo_minuti : null,
        fonte: 'ricetta'
      },
      _recipe_id: recipe.id,
      _recipe_name: recipe.nome_originale || '',
    };
  } catch (e) {
    console.warn('swapSingleRecipe error (slot=' + slot + '):', e && e.message);
    return null;
  }
}
